/**
 * Assistant rail state (SHELL).
 *
 * Owns the two pieces of UI state the panel needs *before* the live agent is
 * wired: whether the rail is open, and the active context scope. Both persist to
 * `localStorage` (frontend-ux-design.md §4.3: "state in localStorage").
 *
 * The Header already dispatches `aos:toggle-assistant` (and binds ⌘J); this hook
 * listens for that event so the toggle works from anywhere without prop-drilling.
 */
import * as React from "react";

import type { AssistantScope } from "@/components/assistant/types";
import { TOGGLE_ASSISTANT_EVENT } from "@/components/layout/Header";

const OPEN_STORAGE_KEY = "aos:assistant:open";
const SCOPE_STORAGE_KEY = "aos:assistant:scope";

/** "Just me" — the always-available fallback scope (no backend needed). */
export const SELF_SCOPE: AssistantScope = {
  kind: "user",
  id: "me",
  label: "Just me",
  contextKey: "user:me",
};

function readOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(OPEN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function readScope(): AssistantScope {
  if (typeof window === "undefined") return SELF_SCOPE;
  try {
    const raw = window.localStorage.getItem(SCOPE_STORAGE_KEY);
    if (!raw) return SELF_SCOPE;
    const parsed = JSON.parse(raw) as Partial<AssistantScope>;
    if (parsed && parsed.kind && parsed.id && parsed.contextKey && parsed.label) {
      return parsed as AssistantScope;
    }
  } catch {
    /* fall through to default */
  }
  return SELF_SCOPE;
}

export interface AssistantState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  scope: AssistantScope;
  setScope: (scope: AssistantScope) => void;
}

export function useAssistantState(): AssistantState {
  const [open, setOpenState] = React.useState<boolean>(readOpen);
  const [scope, setScopeState] = React.useState<AssistantScope>(readScope);

  const setOpen = React.useCallback((next: boolean) => {
    setOpenState(next);
    try {
      window.localStorage.setItem(OPEN_STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* storage unavailable (private mode) — keep in-memory state */
    }
  }, []);

  const toggle = React.useCallback(() => {
    setOpenState((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(OPEN_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const setScope = React.useCallback((next: AssistantScope) => {
    setScopeState(next);
    try {
      window.localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);

  // The Header's Sparkles button + ⌘J both dispatch this event.
  React.useEffect(() => {
    const onToggle = () => toggle();
    window.addEventListener(TOGGLE_ASSISTANT_EVENT, onToggle);
    return () => window.removeEventListener(TOGGLE_ASSISTANT_EVENT, onToggle);
  }, [toggle]);

  return { open, setOpen, toggle, scope, setScope };
}
